import { Look } from './catalog';

/** One contribution. Amounts are in USD (convert Pix/BRL when adding the entry). */
export interface GoalEntry {
    /** yyyy-mm-dd. */
    date: string;
    amount: number;
    /** Public, so no names unless the person asked for one. */
    note: string;
}

/** An item that stays locked for everyone until its jar is full, then unlocks for everyone. */
export interface Goal {
    id: string;
    /** The look category and option id this goal unlocks, e.g. hat + wizard. */
    group: keyof Pick<
        Look,
        'hat' | 'neck' | 'eyes' | 'mouth' | 'vine' | 'background'
    >;
    item: string;
    /** The real cost of making the item, in USD. */
    target: number;
    ledger: GoalEntry[];
}

/**
 * The public ledger. Updating it is a commit, so git history is the audit trail: add a line to a
 * goal's `ledger` when a Ko-fi or Mercado Pago contribution arrives, and push.
 * (Store purchases and ad revenue do not count toward goals.)
 */
export const GOALS: Goal[] = [
    {
        id: 'wizard-hat',
        group: 'hat',
        item: 'wizard',
        target: 30,
        ledger: [],
    },
];

export const raised = (goal: Goal): number =>
    goal.ledger.reduce((sum, e) => sum + e.amount, 0);

export const isFunded = (goal: Goal): boolean => raised(goal) >= goal.target;

/** 0..1, for the meter. */
export const progressOf = (goal: Goal): number =>
    goal.target > 0 ? Math.min(1, raised(goal) / goal.target) : 1;

/** The goal that gates this option, if it exists and is not yet funded. */
export const lockingGoal = (
    group: Goal['group'],
    item: string,
    goals: Goal[] = GOALS
): Goal | undefined =>
    goals.find((g) => g.group === group && g.item === item && !isFunded(g));

/** Replaces any still-locked option with its "none"/default, e.g. a saved look from before the item was gated. */
export const unlockedLook = (look: Look, goals: Goal[] = GOALS): Look => {
    let result = look;
    for (const g of goals) {
        if (!isFunded(g) && result[g.group] === g.item) {
            result = {
                ...result,
                [g.group]:
                    g.group === 'background'
                        ? 'default'
                        : g.group === 'vine'
                          ? 'classic'
                          : 'none',
            };
        }
    }
    return result;
};
